import fs from 'node:fs/promises'
import path from 'node:path'
import { parseDocument } from 'yaml'
import { type CatalogsConfig, parseCatalogsConfig } from '../types/CatalogConfig'
import { type Index } from '../types/IndexType'
import { type Options } from '../types/Options'
import { type PackageFile } from '../types/PackageFile'
import { type VersionSpec } from '../types/VersionSpec'
import { escapeRegExp } from './escapeRegExp'
import resolveDepSections from './resolveDepSections'
import { upgradeJsonCatalogDependencies } from './upgradeJsonCatalogDependencies'
import { updateYamlCatalogDependencies } from './upgradeYamlCatalogDependencies'

/**
 * Upgrade the dependency declarations in the package data.
 *
 * @param pkgData The package.json data, as utf8 text
 * @param current Old dependencies {package: range}
 * @param upgraded New dependencies {package: range}
 * @param options Options object
 * @param pkgFile Optional path to the package file
 * @returns The updated package data, as utf8 text
 * @description Side Effect: prompts
 */
async function upgradePackageData(
  pkgData: string,
  current: Index<VersionSpec>,
  upgraded: Index<VersionSpec>,
  options: Options,
  pkgFile?: string,
) {
  // Check if this is a catalog file (pnpm-workspace.yaml or package.json with catalogs)
  if (pkgFile) {
    const fileName = path.basename(pkgFile)
    const fileExtension = path.extname(pkgFile)

    // Handle synthetic catalog files (package.json#catalog format)
    if (pkgFile.includes('#catalog')) {
      // This is a synthetic catalog file, we need to read and update the actual file
      const actualFilePath = pkgFile.replace('#catalog', '')
      const actualFileExtension = path.extname(actualFilePath)

      if (actualFileExtension === '.json') {
        // Bun format: update package.json catalogs and return the updated content
        return upgradeJsonCatalogDependencies(actualFilePath, current, upgraded)
      }
    }

    // Handle yaml catalog files
    if (fileName === 'pnpm-workspace.yaml' || fileName === '.yarnrc.yml') {
      const yamlContent = await fs.readFile(pkgFile, 'utf-8')
      const catalogData: CatalogsConfig = parseCatalogsConfig(parseDocument(yamlContent).toJSON())

      // Reconstruct the list of updates to apply unfortunately we lost the path information during extraction before
      const reconstructedUpdates: { path: string[]; newValue: string }[] = []

      if (catalogData.catalogs) {
        for (const [catalogName, catalog] of Object.entries(catalogData.catalogs)) {
          for (const [dep, version] of Object.entries(upgraded)) {
            if (catalog[dep]) {
              reconstructedUpdates.push({ path: ['catalogs', catalogName, dep], newValue: version })
            }
          }
        }
      }

      if (catalogData.catalog) {
        for (const [dep, version] of Object.entries(upgraded)) {
          if (catalogData.catalog?.[dep]) {
            reconstructedUpdates.push({ path: ['catalog', dep], newValue: version })
          }
        }
      }

      // Handle nested workspaces.catalog and workspaces.catalogs format
      const workspacesData = catalogData.workspaces
      if (workspacesData && !Array.isArray(workspacesData)) {
        if (workspacesData.catalogs) {
          for (const [catalogName, catalog] of Object.entries(workspacesData.catalogs)) {
            for (const [dep, version] of Object.entries(upgraded)) {
              if (catalog[dep]) {
                reconstructedUpdates.push({ path: ['workspaces', 'catalogs', catalogName, dep], newValue: version })
              }
            }
          }
        }
        if (workspacesData.catalog) {
          for (const [dep, version] of Object.entries(upgraded)) {
            if (workspacesData.catalog?.[dep]) {
              reconstructedUpdates.push({ path: ['workspaces', 'catalog', dep], newValue: version })
            }
          }
        }
      }

      let updatedContent = yamlContent
      for (const upgrade of reconstructedUpdates) {
        const updatedYaml = updateYamlCatalogDependencies({
          fileContent: updatedContent,
          upgrade,
          options,
          filePath: pkgFile,
        })
        if (updatedYaml) {
          updatedContent = updatedYaml
        }
      }

      return updatedContent
    }

    // Handle package.json catalog files (check if content contains catalog/catalogs at root level or in workspaces)
    if (fileExtension === '.json') {
      const parsed = JSON.parse(pkgData)
      const hasTopLevelCatalogs = parsed.catalog || parsed.catalogs
      const hasWorkspacesCatalogs =
        parsed.workspaces &&
        !Array.isArray(parsed.workspaces) &&
        (parsed.workspaces.catalog || parsed.workspaces.catalogs)

      if (hasTopLevelCatalogs || hasWorkspacesCatalogs) {
        return upgradeJsonCatalogDependencies(pkgFile, current, upgraded)
      }
    }
  }

  // Always include overrides since any upgraded dependencies needed to be upgraded in overrides as well.
  // https://github.com/raineorshine/npm-check-updates/issues/1332
  const depSections = [...resolveDepSections(options.dep), 'overrides']

  // iterate through each dependency section
  const sectionRegExp = new RegExp(`"(${depSections.join(`|`)})"s*:[^}]*`, 'g')
  let newPkgData = pkgData.replace(sectionRegExp, section => {
    // replace each upgraded dependency in the section
    return Object.entries(upgraded).reduce((updatedSection, [dep]) => {
      // const expression = `"${dep}"\\s*:\\s*"(${escapeRegExp(current[dep])})"`
      const expression = `"${escapeRegExp(dep)}"\\s*:\\s*("|{\\s*"."\\s*:\\s*")(${escapeRegExp(current[dep])})"`
      const regExp = new RegExp(expression, 'g')
      return updatedSection.replace(regExp, (match, child) => `"${dep}${child ? `": ${child}` : ': '}${upgraded[dep]}"`)
    }, section)
  })

  if (depSections.includes('packageManager')) {
    const pkg = JSON.parse(pkgData) as PackageFile
    if (pkg.packageManager) {
      const [name] = pkg.packageManager.split('@')
      if (upgraded[name]) {
        newPkgData = newPkgData.replace(
          /"packageManager"\s*:\s*".*?@[^"]*"/,
          `"packageManager": "${name}@${upgraded[name]}"`,
        )
      }
    }
  }

  return newPkgData
}

export default upgradePackageData
